import type { HTMLAttributes, ReactNode } from 'react'
import { cx } from './cx'

export type ChipTone =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'purple'
  | 'brand'
  /** UID-225 (Parts Library CSS-fidelity pass) — the design's "needs-rfq"
   * readiness-chip violet (#8b5cf6), distinct from the app's resolved
   * brand purple (`purple` tone, #9747FF). Scoped to the Parts Library
   * listing's needs-rfq/"In RFQ" chips (PartsTable.tsx) — every other
   * `purple`-toned chip elsewhere in the app is untouched. */
  | 'violet'
  /** UID-225 — the design's "needs-match" readiness-chip neutral slate
   * (#6B7280/#F1F2F4/#DADCE0), distinct from both `neutral` (lighter grey)
   * and `error` (the pre-UID-225 tone this state incorrectly used). Scoped
   * to the Parts Library listing's needs-match chip (PartsTable.tsx). */
  | 'slate'

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  readonly tone?: ChipTone
  /** Decorative status glyph shown before the label. Status is NEVER
   * conveyed by color alone (components.md "Chip / pill / tag";
   * accessibility-wcag skill) — pair every colored chip with an icon and/or
   * explicit text, never color as the sole signal. */
  readonly icon?: ReactNode
  readonly children: ReactNode
}

/**
 * Status pill / category chip / badge primitive (components.md "Chip /
 * pill / tag" — draft/in-review/approved/ordered status pills, "In
 * stock"/"Made to order" dot+label, "Quote required"/"Locked" badges).
 * `--radius-pill`, one shared shape for every tone; the tone only changes
 * fg/bg/border, never removes the text label.
 */
export function Chip({ tone = 'neutral', icon, children, className, ...rest }: ChipProps) {
  const classes = cx('chip', `chip-${tone}`, className)
  // Forward `...rest` so a wrapping Tooltip's injected handlers +
  // `aria-describedby` reach the rendered element instead of being silently
  // dropped (WCAG F1) — without this, a Tooltip around a Chip is inert for
  // assistive tech and pointer/focus users alike.
  return (
    <span className={classes} {...rest}>
      {icon && (
        <span className="chip-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </span>
  )
}
